import { STYLE_SHEET_IDS } from "@client/lib/styleSheet";
import { type BaseAppState, type Context, type GenericMap, type LocalSettings, type WithPages } from "@client/types";
import { type ComponentAppState } from "@client/types/AppState/component";
import { type LabelsAppState } from "@client/types/AppState/labels";
import { type LayoutAppState } from "@client/types/AppState/layouts";
import { type PageAppState } from "@client/types/AppState/page";
import { type PageInfo, type PageSubType, type PageType } from "@client/types/AppState/pages";
import { type PortalAppState } from "@client/types/AppState/portal";
import { type StyleSheetAppState } from "@client/types/AppState/styleSheet";
import { type StyleSheetData, type StyleSheetSettings, type StyleSheetStructureItem, type StyleSheetType } from "@client/types/StyleSheet";
import { toMap } from "@common/lib/array";
import { nodesToPlain } from "@common/lib/tree";
import { createSelector } from "reselect";

export const contentEntitiesSelector = (state: BaseAppState) => state.content.entities;
export const contentLocalSelector = (state: BaseAppState) => state.content.local;

export const activeEventIdSelector = (state: BaseAppState) => state.events.activeEventId;
export const componentsSelector = (state: BaseAppState): ComponentAppState[] => state.components;
export const contextSelector = (state: BaseAppState): Context => state.context;
export const cultureCodeSelector = (state: BaseAppState): string => state.cultureCode;
export const programIdSelector = (state: BaseAppState): string | null => state.portal.programId;
export const eventIdSelector = (state: BaseAppState) => state.portal.eventId;
export const eventsItemsSelector = (state: BaseAppState) => state.events.items;
export const eventsSelector = (state: BaseAppState) => state.events;
export const funnelInfoSelector = (state: BaseAppState) => state.funnelInfo;
export const isEditModeSelector = (state: BaseAppState) => state.isEditMode;
export const labelsSelector = (state: BaseAppState): LabelsAppState => state.labels;
export const layoutsSelector = (state: BaseAppState): LayoutAppState[] => state.layouts;
export const modulesSelector = (state: BaseAppState) => state.modules;
export const pageSelector = (state: BaseAppState): PageAppState => state.page;
export const pageIdSelector = (state: BaseAppState): string | null => state.page.id;
export const pageLayoutIdSelector = (state: BaseAppState): string | null => state.page.layoutId;
export const pagesSelector = (state: WithPages) => state.pages;
export const pagesItemsSelector = (state: WithPages): PageInfo[] => state.pages.items;
export const pagesHomePageIdSelector = (state: WithPages): string => state.pages.homePageId;
export const pageSubTypeSelector = (state: BaseAppState): PageSubType | null => state.page.subType;
export const pageTypeSelector = (state: BaseAppState): PageType => state.page.type;
export const pageVariantIdSelector = (state: BaseAppState): string => state.page.variantId;
export const pagePopupsSelector = (state: BaseAppState) => state.pagePopups;
export const popupsSelector = (state: BaseAppState) => state.popups;
export const portalSelector = (state: BaseAppState): PortalAppState => state.portal;
export const portalIdSelector = (state: BaseAppState): string => state.portal.id;
export const screenSelector = (state: BaseAppState) => state.viewport.screen;
export const styleSheetSelector = (state: BaseAppState): StyleSheetAppState => state.styleSheet;
export const styleSheetIdSelector = (state: BaseAppState): string => state.styleSheet.id;
export const styleSheetDataSelector = (state: BaseAppState): StyleSheetData => state.styleSheet.data;
export const styleSheetDataSettingsSelector = (state: BaseAppState): StyleSheetSettings =>
  state.styleSheet.data.settings;
export const styleSheetDataLocalSelector = (state: BaseAppState): LocalSettings => state.styleSheet.local;
export const styleSheetDataStructureSelector = (state: BaseAppState): StyleSheetStructureItem[] =>
  state.styleSheet.data.structure;
export const viewportSelector = (state: BaseAppState) => state.viewport;

export const isProgramSelector = (state: Pick<BaseAppState, "portal">) => !!state.portal.programId;

export const page = {
  id: (state: BaseAppState) => state.page.id,
  layoutId: (state: BaseAppState) => state.page.layoutId
};

export const portal = (state: BaseAppState) => state.portal;

export const styleSheetDefaultSettingsSelector = {
  primaryColor: (state: BaseAppState) => state.styleSheet.data.settings[STYLE_SHEET_IDS.defaults.primaryColor],
  textColor: (state: BaseAppState) => state.styleSheet.data.settings[STYLE_SHEET_IDS.defaults.textColor],
  h1: (state: BaseAppState) => state.styleSheet.data.settings[STYLE_SHEET_IDS.defaults.h1],
  h2: (state: BaseAppState) => state.styleSheet.data.settings[STYLE_SHEET_IDS.defaults.h2],
  h3: (state: BaseAppState) => state.styleSheet.data.settings[STYLE_SHEET_IDS.defaults.h3],
  h4: (state: BaseAppState) => state.styleSheet.data.settings[STYLE_SHEET_IDS.defaults.h4],
  h5: (state: BaseAppState) => state.styleSheet.data.settings[STYLE_SHEET_IDS.defaults.h5],
  h6: (state: BaseAppState) => state.styleSheet.data.settings[STYLE_SHEET_IDS.defaults.h6],
  link: (state: BaseAppState) => state.styleSheet.data.settings[STYLE_SHEET_IDS.defaults.link],
  text: (state: BaseAppState) => state.styleSheet.data.settings[STYLE_SHEET_IDS.defaults.text],

  local: {
    primaryColor: (state: BaseAppState) =>
      state.styleSheet.local ? state.styleSheet.local[STYLE_SHEET_IDS.defaults.primaryColor] : null,
    textColor: (state: BaseAppState) =>
      state.styleSheet.local ? state.styleSheet.local[STYLE_SHEET_IDS.defaults.textColor] : null,
    h1: (state: BaseAppState) => (state.styleSheet.local ? state.styleSheet.local[STYLE_SHEET_IDS.defaults.h1] : null),
    h2: (state: BaseAppState) => (state.styleSheet.local ? state.styleSheet.local[STYLE_SHEET_IDS.defaults.h2] : null),
    h3: (state: BaseAppState) => (state.styleSheet.local ? state.styleSheet.local[STYLE_SHEET_IDS.defaults.h3] : null),
    h4: (state: BaseAppState) => (state.styleSheet.local ? state.styleSheet.local[STYLE_SHEET_IDS.defaults.h4] : null),
    h5: (state: BaseAppState) => (state.styleSheet.local ? state.styleSheet.local[STYLE_SHEET_IDS.defaults.h5] : null),
    h6: (state: BaseAppState) => (state.styleSheet.local ? state.styleSheet.local[STYLE_SHEET_IDS.defaults.h6] : null),
    text: (state: BaseAppState) =>
      state.styleSheet.local ? state.styleSheet.local[STYLE_SHEET_IDS.defaults.text] : null,
    link: (state: BaseAppState) =>
      state.styleSheet.local ? state.styleSheet.local[STYLE_SHEET_IDS.defaults.link] : null
  }
};

export const activePopupSelector = createSelector([popupsSelector], (popups) =>
  popups.find((x) => x.state !== "closed")
);
export const styleSheetIdToTypeSelector = createSelector(
  [styleSheetDataStructureSelector],
  (structure): GenericMap<StyleSheetType> =>
    toMap(
      nodesToPlain(structure).filter((x) => x.type),
      (x) => x.id,
      (x) => x.type!
    )
);

export const pageFunnelPathsSelector = createSelector([funnelInfoSelector, pageIdSelector], (fi, pageId) =>
  fi ? fi.funnelPaths.filter((x) => x.originPageId === pageId) : undefined
);
